import React, { type FormEventHandler } from 'react'
import Rx from 'rx'
import { observeImmutable } from '@core/Components/HOCs/observe'
import { InputField } from '@mobi/component-library/Legacy/Input/InputField'
import { SelectField } from '@mobi/component-library/Legacy/Select'
import { CheckboxInput } from '@mobi/component-library/Legacy/Input/CheckboxInput'
import { state$ as reverificationState$ } from '@core/Areas/Reverification/driver'
import {
  state$ as contactDetailsState$,
  ContactDetailsState,
  ValidationErrors,
  ChangeValue,
  ContactDetailsFields,
  ValidateField,
} from '@core/Areas/ContactDetails/driver'
import { CountrySelect } from '../CountrySelect/CountrySelect'
import { HorizontalDivider, SectionTitle, CheckboxContainer } from './AdditionalDetails.styles'
import { Medicare } from './Medicare'
import { PastReverificationMessage } from '@core/Areas/Reverification/Components/PastReverificationMessage'
import { statesInAustralia } from '@mobi/settings/statesInAustralia'
import { ConsentText, TermsAndConditionsLink } from '@mobi/account/Areas/SignUp/Components/Message'
import { useFeature } from '@core/Utils/hooks'
import { FeatureFlags } from '@mobi/settings/featureFlags'

interface AdditionalDetailsFields {
  driversLicenceNumber: string
  driversLicenceState: string
  driversLicenceCardNumber: string
  passportNumber: string
  passportCountry: string
  agreeToVerifyAdditionalDetails: boolean
  medicareBlueYellowExpiryDate: Date | null
  medicareGreenExpiry: string
  medicareCardColour: string
  medicareMiddleNameOnCard: string
  medicareNumber: string
  medicareReference: string
  validationErrors: ValidationErrors
  isReverificationSuccess: boolean
}

export type AdditionalDetailsProps = Pick<ContactDetailsState, keyof AdditionalDetailsFields> // this is for type safety, when ContactDetailsState changes it forces to update AdditionalDetailsFields

const state$ = Rx.Observable.combineLatest(
  contactDetailsState$,
  reverificationState$,
  (contactDetails, reverfication) => ({ contactDetails, reverfication })
)

export const AdditionalDetails = observeImmutable(state$, ({ record }) => {
  const { requiresReverification, hasAttemptedReverification } = record.reverfication

  if (requiresReverification && hasAttemptedReverification) {
    return <AdditionalDetailsComponent {...record.contactDetails.toJS()} />
  }
  return null
})

function validateFields() {
  const validationFields: (keyof ContactDetailsFields)[] = [
    'passportCountry',
    'passportNumber',
    'driversLicenceState',
    'driversLicenceNumber',
    'driversLicenceCardNumber',
  ]
  validationFields.forEach(field => ValidateField({ field }))
}

export const AdditionalDetailsComponent = ({
  driversLicenceNumber,
  driversLicenceState,
  driversLicenceCardNumber,
  passportNumber,
  passportCountry,
  agreeToVerifyAdditionalDetails,
  medicareBlueYellowExpiryDate,
  medicareGreenExpiry,
  medicareCardColour,
  medicareNumber,
  medicareReference,
  medicareMiddleNameOnCard,
  validationErrors,
  isReverificationSuccess,
}: AdditionalDetailsProps) => {
  const changeDriversLicenceState: FormEventHandler<HTMLSelectElement> = event =>
    ChangeValue({ field: 'driversLicenceState', value: event.currentTarget.value })
  const changeDriversLicenceNumber: FormEventHandler<HTMLInputElement> = event =>
    ChangeValue({ field: 'driversLicenceNumber', value: event.currentTarget.value })
  const changeDriversLicenceCardNumber: FormEventHandler<HTMLInputElement> = event =>
    ChangeValue({ field: 'driversLicenceCardNumber', value: event.currentTarget.value })

  const changePassportCountry: FormEventHandler<HTMLSelectElement> = event =>
    ChangeValue({ field: 'passportCountry', value: event.currentTarget.value })
  const changePassportNumber: FormEventHandler<HTMLInputElement> = event =>
    ChangeValue({ field: 'passportNumber', value: event.currentTarget.value })

  const isAmlRiskAssessed = useFeature(FeatureFlags.AML_SIGNUP_RISK_ASSESSMENT.key)

  return (
    <div>
      <PastReverificationMessage isReverificationSuccess={!!isReverificationSuccess} />

      <fieldset>
        <SectionTitle>
          <strong>Driver's licence</strong>
        </SectionTitle>
        <SelectField
          name='State of Issue'
          options={statesInAustralia}
          value={driversLicenceState}
          placeholder='Select state'
          onChange={changeDriversLicenceState}
          onBlur={validateFields}
          errorMessage={validationErrors.driversLicenceState}
        />
        <InputField
          type='text'
          name='Licence Number'
          value={driversLicenceNumber || ''}
          onChange={changeDriversLicenceNumber}
          onBlur={validateFields}
          errorMessage={validationErrors.driversLicenceNumber}
        />
        <InputField
          type='text'
          name='Card Number'
          value={driversLicenceCardNumber || ''}
          onChange={changeDriversLicenceCardNumber}
          onBlur={validateFields}
          errorMessage={validationErrors.driversLicenceCardNumber}
        />
      </fieldset>

      <HorizontalDivider />

      <fieldset>
        <SectionTitle>
          <strong>Passport</strong>
        </SectionTitle>
        <CountrySelect
          name='Country of Issue'
          value={passportCountry}
          onChange={changePassportCountry}
          onBlur={validateFields}
          errorMessage={validationErrors.passportCountry}
        />
        <InputField
          name='Passport Number'
          value={passportNumber || ''}
          onChange={changePassportNumber}
          onBlur={validateFields}
          errorMessage={validationErrors.passportNumber}
        />
      </fieldset>

      <HorizontalDivider />
      {!isAmlRiskAssessed && (
        <>
          <fieldset>
            <SectionTitle>
              <strong>Medicare</strong>
            </SectionTitle>
            <Medicare
              medicareBlueYellowExpiryDate={medicareBlueYellowExpiryDate}
              medicareGreenExpiry={medicareGreenExpiry}
              medicareCardColour={medicareCardColour}
              medicareNumber={medicareNumber}
              medicareReference={medicareReference}
              medicareMiddleNameOnCard={medicareMiddleNameOnCard}
              validationErrors={validationErrors}
            />
          </fieldset>
          <HorizontalDivider />
        </>
      )}

      <AgreeToVerifyAdditionalDetails
        agreeToVerifyAdditionalDetails={agreeToVerifyAdditionalDetails ?? false}
        isAmlRiskAssessed={isAmlRiskAssessed}
      />
    </div>
  )
}

const AgreeToVerifyAdditionalDetails = ({
  agreeToVerifyAdditionalDetails,
  isAmlRiskAssessed,
}: {
  agreeToVerifyAdditionalDetails: boolean
  isAmlRiskAssessed: boolean
}) => {
  const changeAgreeToVerifyAdditionalDetails = (event: React.FormEvent<HTMLInputElement>) =>
    ChangeValue({ field: 'agreeToVerifyAdditionalDetails', value: event.currentTarget.checked })

  return (
    <CheckboxContainer>
      <CheckboxInput
        id='agree-to-verify-checkbox'
        checked={agreeToVerifyAdditionalDetails}
        onChange={changeAgreeToVerifyAdditionalDetails}
        label={
          <>
            <ConsentText isAmlRiskAssessed={isAmlRiskAssessed} /> <br /> <br />{' '}
            {TermsAndConditionsLink}
          </>
        }
      />
    </CheckboxContainer>
  )
}
